import { Box, Grid, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { addigyKeys, integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import TextWithExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import {
  defaultStaleTime,
  PLATFORM_USER_ACTIONS,
  VIEWER_ACCESS_DENIED_MESSAGE,
} from "../../../../utils/constants";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import AddigyConnectForm from "./AddigyConnectForm";
import addigyCreateToken from "./images/Addigy-create-token.png";
import addigyIntegrations from "./images/Addigy-integrtations.png";
import addigyNewAPIToken from "./images/Addigy-new-api-token.png";
import addigySearchPermission from "./images/Addigy-search-permission.png";
import addigyTokenName from "./images/Addigy-token-name.png";

const AddigyIntegration = () => {
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("addigy");
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { checkAddigyPermissions, trackEvent, registerAddigyIntegration } =
    useAPI();
  const colors = useColors();
  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.MODIFY_INTEGRATION
  );
  const enabledFetching = isIntegrated && !loading && hasPermission;

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const handleCheckPermissions = async () => {
    queryClient.invalidateQueries({
      queryKey: addigyKeys.permissionCheck(),
    });
    trackEvent(
      "User clicked on Check Permissions button in Addigy Integration"
    );
  };

  const {
    data: missingPermissions,
    isFetching: isLoadingCheckAddigyPermissions,
  } = useQuery({
    queryKey: addigyKeys.permissionCheck(),
    queryFn: async () => {
      const response = await checkAddigyPermissions();
      return response;
    },
    select: (data) => data.data,

    enabled: enabledFetching,
  });

  // When the page is opened with ?viewonly we lock inputs but show the full guide.
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const onSubmit = async (values) => {
    setFormValues(values);
    setLoading(true);
    registerAddigyIntegration(
      values.credentials.clientConsoleUrl,
      values.credentials.apiToken,
      values.credentials.orgId
    )
      .then((response) => {
        queryClient.invalidateQueries({ queryKey: integrationKeys.all });
        Toast.success("Addigy integration successful");
        // Clear form values on success
        setFormValues(null);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading && !isIntegratedLoading
            ? "Once integrated, the Breeze platform will automatically read the configurations from Addigy and prioritize the suggested remediations."
            : ""
        }
        image={logos.AddigyLogo}
        name={"Addigy"}
        content={
          isIntegratedLoading || loading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Addigy is registered!</Typography>
              {/* Inline Check Permissions Section */}
              <Box
                mt={3}
                mb={2}
                p={2}
                sx={{ background: colors.grey_100, borderRadius: 2 }}
              >
                <Typography variant="h6" gutterBottom>
                  Check Addigy Permissions
                </Typography>
                <CustomToolTipWithPermission
                  title={!hasPermission ? VIEWER_ACCESS_DENIED_MESSAGE : ""}
                  permission={hasPermission}
                  placement="top"
                >
                  <PrimaryButton
                    onClick={handleCheckPermissions}
                    disabled={!hasPermission}
                    sx={{
                      minWidth: 160,
                    }}
                  >
                    {isLoadingCheckAddigyPermissions ? (
                      <LoadingIcon
                        size={24}
                        sx={{ color: colors.common_white }}
                      />
                    ) : (
                      "Check Permissions"
                    )}
                  </PrimaryButton>
                </CustomToolTipWithPermission>
                {isLoadingCheckAddigyPermissions && (
                  <Box mt={2}>
                    <Typography variant="body2">
                      Checking permissions...
                    </Typography>
                  </Box>
                )}
                {missingPermissions?.length > 0 && (
                  <Box mt={2}>
                    <Typography color="error" variant="subtitle1">
                      Please grant the missing permissions to the application:
                    </Typography>
                    {missingPermissions.map((permission, index) => (
                      <Typography key={index}>
                        <b>
                          {index + 1}. {permission}
                        </b>
                      </Typography>
                    ))}
                    <Typography variant="subtitle1">
                      To add the missing permissions click on your API token's
                      name
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: addigyTokenName,
                          altext: "View Only Role",
                        },
                      ]}
                    />
                    <Typography variant="subtitle1">
                      Copy the missing permission to the search bar and mark the
                      result
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: addigySearchPermission,
                          altext: "Select missing permissions",
                        },
                      ]}
                    />
                    <Typography variant="subtitle1">
                      Click the update button, then click the Check Permissions
                      button to verify
                    </Typography>
                  </Box>
                )}
                {!hasPermission && (
                  <Box mt={2}>
                    <Typography color="error" variant="subtitle1">
                      Your role, "Viewer", does not have permission to perform
                      this action.
                    </Typography>
                  </Box>
                )}
                {missingPermissions?.length === 0 &&
                  !isLoadingCheckAddigyPermissions &&
                  hasPermission && (
                    <Box mt={2}>
                      <Typography color="success.main" variant="subtitle1">
                        All required permissions are granted!
                      </Typography>
                    </Box>
                  )}
              </Box>
            </Box>
          ) : (
            <Box>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <GuidelineStepByStep>
                {/* STEP 1 */}
                <GuidelineStep title="Login to Addigy">
                  <TextWithExternalLink
                    text="Navigate to "
                    linkText="Addigy"
                    href="https://app.addigy.com"
                  />
                </GuidelineStep>
                {/* STEP 2 */}
                <GuidelineStep title="Create an Addigy API token">
                  <Typography variant="body2">
                    Click on <b>'Account'</b> -> <b>'Integrations'</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: addigyIntegrations,
                        altext: "Second option",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Scroll all the way down and copy your Org ID (paste it
                    below), then generate a new API token (v2)
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: addigyNewAPIToken,
                        altext: "Click the New API Token button",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2" component="ol" sx={{ pl: 2 }}>
                    <li>In the New API Token dialog, enter a Token Name.</li>
                    <Typography
                      variant={"body2"}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      Name suggestion: <strong>{"breezeApiToken"}</strong>
                      <CustomCopyButton
                        copyTitle={"API Token name"}
                        copyText={"breezeApiToken"}
                      />
                    </Typography>
                    <li>
                      In the Search permissions field, type “View” to filter the
                      permisions.
                    </li>
                    <li>
                      Select all permissions labeled View to match the
                      screenshot.
                    </li>
                    <li>Click Add to create the token.</li>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: addigyCreateToken,
                        altext: "Click the New API Token button",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                {/* STEP 3 */}
                <GuidelineStep title="Enter your Console URL, org-id and token">
                  <Typography variant="body2">
                    Fill the following details and click <b>'Connect'</b>:
                  </Typography>
                  <AddigyConnectForm
                    onSubmit={onSubmit}
                    guideMode={guideMode}
                    savedFormValues={formValues}
                  />
                </GuidelineStep>
              </GuidelineStepByStep>
            </Box>
          )
        }
      />
    </Box>
  );
};

export default AddigyIntegration;
